<admin-app-header></admin-app-header>

<div class="container-fluid mt-4 mb-5 px-4">
    <div class="d-flex align-items-center mb-2">
        <h1 class="mb-0"><i class="fas fa-list-check me-2"></i>PayGate Attempts</h1>
        <span class="badge bg-danger ms-3" *ngIf="recentFailureCount">{{ recentFailureCount }} failed in last 24h</span>
        <span class="badge bg-success ms-3" *ngIf="!loading && !recentFailureCount">No failures in last 24h</span>
    </div>
    <p class="text-muted">
        Every interaction with PayGate — tokenisations, authorisations, settlements and voids — with how the result
        reached us. <span *ngIf="!filterClient">Newest 300.</span>
        <span *ngIf="filterClient">Full history for <strong>{{ clientName(filterClient) }}</strong>.</span>
    </p>

    <div class="row g-2 mb-3">
        <div class="col-md-3">
            <input class="form-control" placeholder="Client, subscription, transaction, token, card..." [(ngModel)]="searchText" />
        </div>
        <div class="col-md-2">
            <select class="form-select" [ngModel]="filterClient" (ngModelChange)="selectClient($event)">
                <option value="">All clients</option>
                <option *ngIf="filterClient && !hasClientOption(filterClient)" [value]="filterClient">{{ clientName(filterClient) }}</option>
                <option *ngFor="let c of sortedClientOptions" [value]="c.key">{{ clientName(c.key) }} ({{ c.count }})</option>
            </select>
        </div>
        <div class="col-md-2">
            <select class="form-select" [(ngModel)]="filterType">
                <option value="">All types</option>
                <option value="tokenise">Tokenise</option>
                <option value="authorise">Authorise</option>
                <option value="settle">Settle</option>
                <option value="void">Void</option>
                <option value="query">Query</option>
                <option value="vaultLookup">Vault lookup</option>
            </select>
        </div>
        <div class="col-md-2">
            <select class="form-select" [(ngModel)]="filterSource">
                <option value="">All sources</option>
                <option value="clientCheckout">Client checkout</option>
                <option value="recurringCron">Recurring cron</option>
                <option value="merchantApproval">Merchant approval</option>
                <option value="admin">Admin</option>
                <option value="lab">Lab</option>
                <option value="system">System</option>
            </select>
        </div>
        <div class="col-md-1 d-flex align-items-center">
            <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" id="failuresOnly" [(ngModel)]="failuresOnly" />
                <label class="form-check-label" for="failuresOnly">Failures only</label>
            </div>
        </div>
        <div class="col-md-2 text-end text-muted small d-flex align-items-center justify-content-end">
            Showing {{ filtered.length }} of {{ attempts.length }}
        </div>
    </div>

    <div *ngIf="loading" class="text-muted">Loading...</div>
    <div *ngIf="!loading && !filtered.length" class="alert alert-info">Nothing matches those filters.</div>

    <table class="table table-sm align-middle" *ngIf="filtered.length">
        <thead>
            <tr>
                <th>When</th>
                <th>Client</th>
                <th>Type</th>
                <th>Via</th>
                <th>Source</th>
                <th>Amount</th>
                <th>Card</th>
                <th>Result</th>
                <th></th>
            </tr>
        </thead>
        <tbody>
            <ng-container *ngFor="let a of filtered">
                <tr [class.table-danger]="!a.success">
                    <td class="text-nowrap small">{{ toDate(a.createdAt) | date: 'dd MMM HH:mm:ss' }}</td>
                    <td class="small">
                        <ng-container *ngIf="a.clientKey; else noClient">
                            <a href="javascript:void(0)" (click)="selectClient(a.clientKey!)" title="Show only this client's attempts">{{ clientName(a.clientKey) }}</a>
                            <div class="text-muted font-monospace" style="font-size: 0.75em">{{ a.clientKey }}</div>
                        </ng-container>
                        <ng-template #noClient><span class="text-muted">—</span></ng-template>
                    </td>
                    <td><span class="badge" [ngClass]="badgeClass(a)">{{ a.type }}</span></td>
                    <td>
                        <span class="badge" [ngClass]="outcomeClass(a)">{{ a.outcomeSource }}</span>
                        <small class="text-muted ms-1">{{ a.channel }}</small>
                    </td>
                    <td class="small">{{ a.source }}</td>
                    <td class="small text-nowrap">{{ asAmount(a.amountInCents) }}</td>
                    <td class="small text-nowrap">{{ a.maskedCard || '—' }}</td>
                    <td class="small">
                        <span *ngIf="a.success" class="text-success">{{ a.resultDescription || 'OK' }}</span>
                        <span *ngIf="!a.success" class="text-danger">
                            {{ a.resultCode }} {{ a.resultDescription || a.errorMessage }}
                        </span>
                        <span *ngIf="a.sentCvv" class="badge bg-warning text-dark ms-1" title="A CVV was sent — should be false after the migration">CVV</span>
                        <span *ngIf="a.recurringType" class="badge bg-light text-dark border ms-1">{{ a.recurringType }}/{{ a.initiatedType }}</span>
                    </td>
                    <td class="text-end">
                        <button class="btn btn-sm btn-link px-0" (click)="toggle(a.$key)">
                            {{ expanded[a.$key || ''] ? 'Hide' : 'Details' }}
                        </button>
                    </td>
                </tr>
                <tr *ngIf="expanded[a.$key || '']">
                    <td colspan="9" class="bg-light">
                        <div class="row small">
                            <div class="col-md-6">
                                <div>
                                    <strong>Client:</strong>
                                    <a *ngIf="a.clientKey" [routerLink]="['/client/', a.clientKey]">{{ clientName(a.clientKey) }}</a>
                                    <span *ngIf="a.clientKey" class="text-muted ms-1">({{ a.clientKey }})</span>
                                    <span *ngIf="!a.clientKey">—</span>
                                </div>
                                <div><strong>Subscription:</strong> {{ a.subscriptionKey || '—' }}</div>
                                <div><strong>Payment:</strong> {{ a.paymentKey || '—' }}</div>
                                <div><strong>Reference:</strong> {{ a.reference || '—' }}</div>
                            </div>
                            <div class="col-md-6">
                                <div><strong>Transaction:</strong> {{ a.transactionId || '—' }}</div>
                                <div><strong>PayRequest:</strong> {{ a.payRequestId || '—' }}</div>
                                <div><strong>Vault token:</strong> {{ a.vaultId || '—' }}</div>
                                <div>
                                    <strong>PayGate ID:</strong> {{ a.payGateId || '—' }}
                                    <span class="ms-2"><strong>Risk:</strong> {{ a.riskIndicator || '—' }}</span>
                                    <span class="ms-2"><strong>Took:</strong> {{ a.durationMs ? a.durationMs + 'ms' : '—' }}</span>
                                </div>
                            </div>
                        </div>
                        <pre class="small bg-white border p-2 mt-2 mb-0" style="max-height: 260px; overflow: auto">{{ a.rawResponse || '(no payload recorded)' }}</pre>
                    </td>
                </tr>
            </ng-container>
        </tbody>
    </table>
</div>
